import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./custom-sequence.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";
import TabMenu from "../tabmenu/TabMenu";

/** Extract words from PHP-style serialized string (fallback). */
const parseSerializedWords = (str) => {
  if (typeof str !== "string" || !str.includes('s:') || !str.includes('"')) return null;
  const regex = /s:\d+:"([^"]*)";/g;
  const out = [];
  let m;
  while ((m = regex.exec(str)) !== null) {
    if (m[1].trim()) out.push(m[1].trim());
  }
  return out.length > 0 ? out : null;
};

const parseArray = (val) => {
  if (Array.isArray(val)) return val.map(String);
  if (typeof val === "string") {
    const serialized = parseSerializedWords(val);
    if (serialized) return serialized;
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p.map(String) : [];
    } catch {
      return val.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
};

const COLS = 5;

const CustomSequence = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [valuesArray, setValuesArray] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState([]); // 1-based indices in click order
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setValuesArray(parseArray(data.values_array));
    setSelectedOrder([]);
  }, [data]);

  const totalCount = valuesArray.length;
  const allSelected = totalCount > 0 && selectedOrder.length === totalCount;

  const handleCellClick = (oneBasedIndex) => {
    const idx = selectedOrder.indexOf(oneBasedIndex);
    if (idx === -1) {
      setSelectedOrder((prev) => [...prev, oneBasedIndex]);
    } else {
      setSelectedOrder((prev) => {
        const next = prev.filter((x) => x !== oneBasedIndex);
        return next;
      });
    }
  };

  const getSelectionNumber = (oneBasedIndex) => {
    const pos = selectedOrder.indexOf(oneBasedIndex);
    return pos === -1 ? null : pos + 1;
  };

  const isSelected = (oneBasedIndex) => selectedOrder.includes(oneBasedIndex);

  const handleSave = async () => {
    if (!allSelected) return;
    setLoading(true);
    try {
      const newShuffle = selectedOrder.map(String);
      await updateThemeData({
        payload: {
          data: { shuffle: newShuffle },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Custom sequence saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedOrder([]);
  };

  const rows = useMemo(() => {
    const r = [];
    for (let i = 0; i < valuesArray.length; i += COLS) {
      r.push(valuesArray.slice(i, i + COLS));
    }
    return r;
  }, [valuesArray]);

  if (status === "loading" && !data) {
    return (
      <div className="customsequence-page">
        <div className="customsequence-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <TabMenu />

      <div className="customsequence-page">
        <div className="customsequence-card">
          <div className="customsequence-card-header">Click on words/numbers</div>
          <div className="customsequence-card-body">
            <p className="customsequence-hint">
              Click cells in the order you want them to be released. When all are selected, save the custom sequence.
            </p>
            <div className="customsequence-table-wrap">
              <table className="customsequence-table">
                <tbody>
                  {rows.map((rowCells, rowIdx) => (
                    <tr key={rowIdx}>
                      {rowCells.map((cellVal, colIdx) => {
                        const zeroBased = rowIdx * COLS + colIdx;
                        const oneBased = zeroBased + 1;
                        const selected = isSelected(oneBased);
                        const orderNum = getSelectionNumber(oneBased);
                        return (
                          <td
                            key={zeroBased}
                            className={`customsequence-cell ${selected ? "customsequence-cell-selected" : ""}`}
                            onClick={() => handleCellClick(oneBased)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleCellClick(oneBased);
                              }
                            }}
                          >
                            <span className="customsequence-cell-text">{cellVal}</span>
                            {orderNum != null && (
                              <span className="customsequence-cell-order">{orderNum}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="customsequence-actions">
              {allSelected && (
                <button
                  type="button"
                  className="customsequence-btn customsequence-btn-primary"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              )}
              <button
                type="button"
                className="customsequence-btn customsequence-btn-reset"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomSequence;
